import type { ReactNode } from 'react';
import { useCallback, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate } from 'react-router-dom';
import { DailyProvider, useParticipantIds, useVideoTrack } from '@daily-co/daily-react';
import { DailyCallService, useDailyCallState } from '@services/dailycall';
import SentryService from '@services/SentryService';
import { useEventTracking } from '@services/statsig';
import { useAppDispatch, useAppSelector } from '@store/hooks';
import { useSnackbar } from 'notistack';

import { useRecordingContext } from '../../../context/RecordingContext';
import { CallPanel } from '../components/CallPanel';
import { CompactPipView } from '../components/pip/CompactPipView';
import { PipStyleProvider } from '../components/pip/PipStyleProvider';
import { PipView } from '../components/pip/PipView';
import { PipErrorBoundary } from '../components/PipErrorBoundary';
import { usePatientLink } from '../hooks/usePatientLink';
import { usePictureInPicture } from '../hooks/usePictureInPicture';
import {
  addChatMessage,
  clearActiveSession,
  clearChatMessages,
  enterPreCall,
  exitPreCall,
  selectIsInPreCall,
  setActiveSession,
  setChatPanelOpen,
  useCreateProviderJoinLink,
} from '../store';
import type { ChatMessage, TelehealthSession } from '../types';

import type { JoinCallParams, TelehealthCallManagerContextValue, TelehealthPhase } from './TelehealthCallManagerContext';
import { TelehealthCallManagerContext } from './TelehealthCallManagerContext';

export function TelehealthCallManager({ children }: { children: ReactNode }) {
  const dispatch = useAppDispatch();
  const { enqueueSnackbar } = useSnackbar();
  const { telehealth: telehealthTracking } = useEventTracking();
  const navigate = useNavigate();

  // Recording context integration
  const {
    setTelehealthSession: setRecordingTelehealthSession,
    cleanupTelehealthSession,
    requestRemoteStop,
    recordingStatus,
    audioInputDeviceService,
    setSelectedMicrophone,
    initiateRecording,
    pauseRecording,
    resumeRecording,
  } = useRecordingContext();

  // State
  const [session, setSession] = useState<TelehealthSession | null>(null);
  const [isJoining, setIsJoining] = useState(false);
  const dailyCallServiceRef = useRef<DailyCallService | null>(null);
  const isInPreCall = useAppSelector(selectIsInPreCall);
  const [createJoinLink] = useCreateProviderJoinLink();

  // Subscribe to DailyCallService state for Media Session config
  const callState = useDailyCallState(dailyCallServiceRef.current);

  // Remote video track for hidden <video> (Safari classic PiP + Chrome auto-PiP)
  const remoteParticipantIds = useParticipantIds({ filter: 'remote' });
  const remoteVideoState = useVideoTrack(remoteParticipantIds[0] ?? '');

  // Derived
  const phase: TelehealthPhase = !session
    ? isJoining
      ? 'joining'
      : 'idle'
    : isInPreCall
      ? 'precall'
      : 'call';

  const callObject = session
    ? dailyCallServiceRef.current?.getCallObject() ?? null
    : null;

  const activeEncounterId = session?.encounterId ?? null;
  const activeAppointmentId = session?.appointmentId ?? null;

  // Patient link (survives navigation since session is in manager)
  const patientLink = usePatientLink({
    appointmentId: session?.appointmentId,
    encounterId: session?.encounterId,
  });

  // --- PiP ---

  // Handler refs for Media Session — avoids stale closures in usePictureInPicture effects
  const toggleMicRef = useRef<() => void>(() => {});
  const toggleVideoRef = useRef<() => void>(() => {});
  const endCallRef = useRef<() => void>(() => {});

  const pip = usePictureInPicture({
    mediaSession: {
      enabled: phase === 'call',
      onToggleMic: () => toggleMicRef.current(),
      onToggleCamera: () => toggleVideoRef.current(),
      onHangup: () => endCallRef.current(),
      isMicActive: callState.localAudioEnabled,
      isCameraActive: callState.localVideoEnabled,
    },
    onClassicTrackLost: () => remoteVideoState?.persistentTrack ?? null,
  });

  // Mini-player visibility: shows on all pages during active call (PiP takes priority)
  const shouldShowCallPanel = phase === 'call' && !pip.isActive;

  // --- Handlers ---

  const handleLeaveInternal = useCallback(async () => {
    // Stop recording before cleaning up telehealth session.
    // Read isRecording from localStorage (source of truth) to avoid stale closures.
    // Uses requestRemoteStop because it reads ownership from localStorage and calls
    // performStopRecording via ref — avoids stale closure issues.
    const storedStatus = localStorage.getItem('recordingStatus');
    const isCurrentlyRecording = storedStatus
      ? (JSON.parse(storedStatus) as { isRecording?: boolean }).isRecording === true
      : false;

    if (isCurrentlyRecording) {
      try {
        await requestRemoteStop();
      } catch {
        // Best-effort stop — proceed with cleanup regardless
      }
    }
    cleanupTelehealthSession();
    dailyCallServiceRef.current = null;
    setSession(null);
    dispatch(clearActiveSession());
    dispatch(exitPreCall());
    dispatch(clearChatMessages());
    dispatch(setChatPanelOpen(false));
  }, [cleanupTelehealthSession, dispatch, requestRemoteStop]);

  // Auto-leave call when recording stops (e.g. remote tab stopped the recording).
  // In telehealth mode, the call and recording are unified — if the recording ends,
  // the call should end too.
  const wasRecordingRef = useRef(false);
  useEffect(() => {
    if (recordingStatus.isRecording) {
      wasRecordingRef.current = true;
      return;
    }
    // Recording just stopped while in an active call → leave
    if (wasRecordingRef.current && phase === 'call' && session) {
      wasRecordingRef.current = false;
      // Defer to avoid acting during the stop transition
      const timer = setTimeout(() => {
        cleanupTelehealthSession();
        dailyCallServiceRef.current = null;
        setSession(null);
        dispatch(clearActiveSession());
        dispatch(exitPreCall());
        dispatch(clearChatMessages());
        dispatch(setChatPanelOpen(false));
      }, 500);
      return () => clearTimeout(timer);
    }
    wasRecordingRef.current = false;
  }, [recordingStatus.isRecording, phase, session, cleanupTelehealthSession, dispatch]);

  // Sync handler refs after handler definitions
  toggleMicRef.current = () => {
    dailyCallServiceRef.current?.setLocalAudio(!callState.localAudioEnabled);
  };
  toggleVideoRef.current = () => {
    dailyCallServiceRef.current?.setLocalVideo(!callState.localVideoEnabled);
  };
  endCallRef.current = () => { void handleLeaveInternal(); };

  // Close PiP and bring opener tab to foreground (for user-initiated close buttons)
  const handlePipClose = useCallback(async () => {
    window.focus();
    await pip.close();
  }, [pip]);

  // Close PiP, open chat panel, and navigate to encounter
  const handleOpenChatFromPip = useCallback(async () => {
    window.focus();
    await pip.close();
    dispatch(setChatPanelOpen(true));
    if (activeEncounterId) {
      navigate(`/encounters/${activeEncounterId}`);
    }
  }, [pip, dispatch, activeEncounterId, navigate]);

  const joinCall = useCallback(
    async ({ appointmentId, encounterId }: JoinCallParams) => {
      if (!appointmentId) return;
      setIsJoining(true);
      try {
        const result = await createJoinLink({
          joinLinkRequest: { appointment_id: appointmentId },
        }).unwrap();

        const service = new DailyCallService();
        service.initialize();
        dailyCallServiceRef.current = service;

        // Wire into recording pipeline
        setRecordingTelehealthSession({
          dailyCallService: service,
          roomUrl: result.room_url,
          token: result.token,
        });

        const newSession: TelehealthSession = {
          appointmentId,
          encounterId,
          roomUrl: result.room_url,
          roomName: result.room_name,
          token: result.token,
          isOwner: result.is_owner,
          expiresAt: result.expires_at ?? null,
        };
        setSession(newSession);
        dispatch(setActiveSession(newSession));
        dispatch(enterPreCall());
      } catch (error) {
        SentryService.captureException(error as Error, {
          tags: { component: 'telehealth_join' },
        });
        enqueueSnackbar('Failed to join telehealth session', {
          variant: 'error',
        });
      } finally {
        setIsJoining(false);
      }
    },
    [
      createJoinLink,
      enqueueSnackbar,
      setRecordingTelehealthSession,
      dispatch,
    ],
  );

  const joinCallDirect = useCallback(
    async ({ appointmentId, encounterId }: JoinCallParams) => {
      if (!appointmentId) return;
      setIsJoining(true);
      try {
        const result = await createJoinLink({
          joinLinkRequest: { appointment_id: appointmentId },
        }).unwrap();

        const service = new DailyCallService();
        service.initialize();
        dailyCallServiceRef.current = service;

        setRecordingTelehealthSession({
          dailyCallService: service,
          roomUrl: result.room_url,
          token: result.token,
        });

        const newSession: TelehealthSession = {
          appointmentId,
          encounterId,
          roomUrl: result.room_url,
          roomName: result.room_name,
          token: result.token,
          isOwner: result.is_owner,
          expiresAt: result.expires_at ?? null,
        };

        // Join Daily room and start recording BEFORE setting session.
        // Session set triggers phase → 'call' which shows CallPanel.
        // By deferring, the join card stays with "Joining..." until everything is ready.
        await service.join(newSession.roomUrl, newSession.token);
        telehealthTracking.callConnected({
          appointment_id: newSession.appointmentId,
          encounter_id: newSession.encounterId,
        });

        // Auto-start recording before revealing the call UI
        try {
          await initiateRecording();
        } catch (recordingError) {
          SentryService.captureException(recordingError as Error, {
            tags: { component: 'telehealth_recording_autostart' },
          });
          enqueueSnackbar('Recording failed to start automatically. You can start it manually.', {
            variant: 'warning',
          });
        }

        // Now reveal the call UI — phase transitions to 'call'
        setSession(newSession);
        dispatch(setActiveSession(newSession));
      } catch (error) {
        SentryService.captureException(error as Error, {
          tags: { component: 'telehealth_join_direct' },
        });
        enqueueSnackbar('Failed to join telehealth session', {
          variant: 'error',
        });
      } finally {
        setIsJoining(false);
      }
    },
    [createJoinLink, enqueueSnackbar, setRecordingTelehealthSession, dispatch, telehealthTracking, initiateRecording],
  );

  const joinDailyRoom = useCallback(async () => {
    if (!dailyCallServiceRef.current || !session) return;
    dispatch(exitPreCall());
    await dailyCallServiceRef.current.join(session.roomUrl, session.token);
    telehealthTracking.callConnected({
      appointment_id: session.appointmentId,
      encounter_id: session.encounterId,
    });

    // Auto-start recording
    try {
      await initiateRecording();
    } catch (recordingError) {
      SentryService.captureException(recordingError as Error, {
        tags: { component: 'telehealth_recording_autostart' },
      });
      enqueueSnackbar('Recording failed to start automatically. You can start it manually.', {
        variant: 'warning',
      });
    }
  }, [dispatch, session, telehealthTracking, initiateRecording, enqueueSnackbar]);

  // Note: callLeft tracking is fired by CallView/CompactPipView before calling this.
  // The manager only handles cleanup — no duplicate tracking.
  const leaveCall = useCallback(() => {
    void handleLeaveInternal();
  }, [handleLeaveInternal]);

  const stopRecordingAndLeave = useCallback(async () => {
    try {
      await requestRemoteStop();
      leaveCall();
    } catch (error) {
      console.error(
        'Failed to stop recording before leaving telehealth:',
        error,
      );
      enqueueSnackbar('Failed to stop recording. Please try again.', {
        variant: 'error',
      });
    }
  }, [requestRemoteStop, leaveCall, enqueueSnackbar]);

  // --- Chat ---

  const sendChatMessage = useCallback(
    (message: ChatMessage) => {
      const co = dailyCallServiceRef.current?.getCallObject();
      if (!co) return;
      try {
        co.sendAppMessage(message, '*');
      } catch {
        // sendAppMessage may fail in local dev (postMessage origin mismatch)
      }
      dispatch(addChatMessage(message));
    },
    [dispatch],
  );

  // Listen for incoming chat messages from the call object
  useEffect(() => {
    if (!callObject) return;

    const handleAppMessage = (event: { data: unknown; fromId: string }) => {
      if (
        event.data &&
        typeof event.data === 'object' &&
        'type' in event.data &&
        (event.data as { type: string }).type === 'chat'
      ) {
        const msg = event.data as ChatMessage;
        // Use Daily's fromId as senderId fallback when the sender didn't include it
        // (e.g. Prebuilt iframe timing — session_id not yet available at send time).
        if (!msg.senderId && event.fromId) {
          msg.senderId = event.fromId;
        }
        dispatch(addChatMessage(msg));
      }
    };

    callObject.on('app-message', handleAppMessage);
    return () => {
      callObject.off('app-message', handleAppMessage);
    };
  }, [callObject, dispatch]);

  // --- Effects ---

  // Detect unexpected call drops (DailyCallService status → idle while in call)
  useEffect(() => {
    const service = dailyCallServiceRef.current;
    if (!service || phase !== 'call') return;

    const unsubscribe = service.subscribe(() => {
      const state = service.getSnapshot();
      if (state.status === 'idle') {
        // Unexpected disconnect — clean up
        void handleLeaveInternal();
        enqueueSnackbar('Telehealth call ended unexpectedly.', {
          variant: 'warning',
        });
      }
    });

    return unsubscribe;

  }, [phase, handleLeaveInternal, enqueueSnackbar]);

  // Auto-close PiP when call ends (phase leaves 'call')
  useEffect(() => {
    if (phase !== 'call' && pip.isActive) {
      pip.close();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [phase]);

  // Attach remote video track to hidden <video> for Safari classic PiP + Chrome auto-PiP
  useEffect(() => {
    if (!pip.videoRef.current) return;
    const track = remoteVideoState?.persistentTrack;
    if (track) {
      pip.videoRef.current.srcObject = new MediaStream([track]);
    } else {
      pip.videoRef.current.srcObject = null;
    }
  }, [pip.videoRef, remoteVideoState?.persistentTrack]);

  // --- Context value ---

  const contextValue: TelehealthCallManagerContextValue = {
    session,
    phase,
    activeEncounterId,
    activeAppointmentId,
    dailyCallService: dailyCallServiceRef.current,
    callObject,
    joinCall,
    joinCallDirect,
    joinDailyRoom,
    leaveCall,
    stopRecordingAndLeave,
    patientLink,
    pip,
    sendChatMessage,
  };

  return (
    <TelehealthCallManagerContext.Provider value={contextValue}>
      <DailyProvider callObject={callObject}>
        {children}

        {/* Mini-player: visible when call active and PiP not active (all pages) */}
        {shouldShowCallPanel &&
          session &&
          activeEncounterId &&
          dailyCallServiceRef.current && (
            <CallPanel
              session={session}
              activeEncounterId={activeEncounterId}
              dailyCallService={dailyCallServiceRef.current}
              isRecording={recordingStatus.isRecording}
              isRecordingStarting={recordingStatus.isStarting}
              isRecordingPaused={recordingStatus.recordingPaused}
              onLeaveCall={leaveCall}
              onPauseRecording={pauseRecording}
              onResumeRecording={resumeRecording}
            />
          )}

        {/* Document PiP portal: visible when PiP is active, regardless of page */}
        {pip.pipWindow &&
          dailyCallServiceRef.current &&
          session &&
          createPortal(
            <PipStyleProvider pipWindow={pip.pipWindow}>
              <PipErrorBoundary onClose={pip.close}>
                {pip.sizeClass === 'compact' ? (
                  <CompactPipView
                    dailyCallService={dailyCallServiceRef.current}
                    isRecording={recordingStatus.isRecording}
                    isRecordingStarting={recordingStatus.isStarting}
                    isRecordingPaused={recordingStatus.recordingPaused}
                    isOwner={session.isOwner}
                    appointmentId={session.appointmentId}
                    onLeave={() => { void handleLeaveInternal(); }}
                    onClose={handlePipClose}
                    onOpenChat={handleOpenChatFromPip}
                    onPauseRecording={pauseRecording}
                    onResumeRecording={resumeRecording}
                  />
                ) : (
                  <PipView
                    dailyCallService={dailyCallServiceRef.current}
                    audioInputDeviceService={audioInputDeviceService}
                    setSelectedMicrophone={setSelectedMicrophone}
                    pipWindow={pip.pipWindow}
                    isRecording={recordingStatus.isRecording}
                    isRecordingStarting={recordingStatus.isStarting}
                    isRecordingPaused={recordingStatus.recordingPaused}
                    session={session}
                    onLeave={() => { void handleLeaveInternal(); }}
                    onClose={handlePipClose}
                    isPipWindow
                    onPauseRecording={pauseRecording}
                    onResumeRecording={resumeRecording}
                  />
                )}
              </PipErrorBoundary>
            </PipStyleProvider>,
            pip.pipWindow.document.body,
          )}

        {/* Hidden video for Safari classic PiP + Chrome auto-PiP signal */}
        <video
          ref={pip.videoRef}
          autoPlay
          playsInline
          muted
          style={{ position: 'absolute', opacity: 0, pointerEvents: 'none', width: 1, height: 1 }}
        />
      </DailyProvider>
    </TelehealthCallManagerContext.Provider>
  );
}
